import React from 'react';
import {
	Box,
	Typography,
	Grid,
	TextField,
	Checkbox,
	FormControlLabel,
	Paper,
	Divider,
	CircularProgress,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useAuth } from 'module/auth/context/AuthContext';
import { UserAvailability } from 'core/interfaces/userInterface';
import { apiService } from 'core/services/apiService';

interface AvailabilityFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	formRef?: React.MutableRefObject<any>;
	userId?: number;
}

const validationSchema = Yup.object({
	availability: Yup.array(),
	notes: Yup.string().required('Reason for change is required'),
});

const AvailabilityForm: React.FC<AvailabilityFormProps> = ({
	onSubmit,
	onCancel: _onCancel,
	formRef,
	userId,
}) => {
	const { showToast } = useToastContext();
	const { user } = useAuth();
	const currentUserId = user?.id || parseInt(localStorage.getItem('userId') || '0');

	// Fetch days for availability picker
	const { data: daysData, isLoading: daysLoading } = useQuery(['days'], () => getDays());
	
	// Ensure all 7 days of the week are available, even if API only returns workweek
	const allDays = React.useMemo(() => {
		if (!daysData) return [];
		
		// Define all 7 days of the week with their standard positions
		const allDaysOfWeek = [
			{ label: 'Sunday', position: '0' },
			{ label: 'Monday', position: '1' },
			{ label: 'Tuesday', position: '2' },
			{ label: 'Wednesday', position: '3' },
			{ label: 'Thursday', position: '4' },
			{ label: 'Friday', position: '5' },
			{ label: 'Saturday', position: '6' },
		];
		
		// Create a map of existing days from API
		const existingDaysMap = new Map(
			daysData.map(day => [day.label, day])
		);
		
		// Build complete list with all 7 days, using API data where available
		return allDaysOfWeek.map(dayInfo => {
			const existingDay = existingDaysMap.get(dayInfo.label);
			if (existingDay) {
				return existingDay;
			}
			// Create a placeholder for missing days (like Saturday/Sunday)
			// These will have a temporary @id that can be used for form state
			return {
				'@id': `/api/days/${dayInfo.position}`,
				id: parseInt(dayInfo.position),
				label: dayInfo.label,
				locale: dayInfo.label.toLowerCase(),
				position: dayInfo.position,
			};
		});
	}, [daysData]);
	
	// Fetch user's data with availability (refetch on mount to ensure fresh data)
	// Use userId prop if provided (from user settings), otherwise use current user
	const targetUserId = userId || currentUserId;
	const { data: userData, isLoading: userLoading } = useQuery(
		['user-availability', targetUserId],
		async () => {
			const endpoint = userId ? `/users/${userId}` : '/users/me';
			const response = await apiService.get(endpoint);
			return apiService.responseHandler(response);
		},
		{
			enabled: !!targetUserId,
			refetchOnMount: 'always', // Always refetch when component mounts
			staleTime: 0, // Consider data stale immediately
		}
	);
	
	// Get user's current availability from the fresh query data
	const currentAvailability: UserAvailability[] = React.useMemo(
		() => userData?.userAvailability || user?.userAvailability || [],
		[userData?.userAvailability, user?.userAvailability]
	);

	// Store initial values in a ref to compare for changes
	const initialValuesRef = React.useRef<any>(null);

	const handleSubmit = async (values: any) => {
		// Validate that at least one day is selected
		const enabledDays = values.availability.filter((d: any) => d.enabled);

		if (enabledDays.length === 0) {
			showToast('error', 'Please select at least one day');
			return;
		}

		// Validate that all enabled days have start and end times
		const invalidDays = values.availability.filter(
			(d: any) => d.enabled && (!d.startTime || !d.endTime)
		);

		if (invalidDays.length > 0) {
			const dayNames = invalidDays.map((d: any) => d.day.label).join(', ');
			showToast('error', `Please set start and end times for: ${dayNames}`);
			return;
		}

		// Only send requests for days that have changed
		const changedDays = values.availability.filter((d: any, index: number) => {
			const initial = initialValuesRef.current?.availability[index];
			if (!initial) return d.enabled; // If no initial, include if enabled

			// Check if enabled status changed
			if (d.enabled !== initial.enabled) return true;

			// If enabled, check if times changed
			if (d.enabled) {
				const startChanged =
					d.startTime && initial.startTime
						? !dayjs(d.startTime).isSame(dayjs(initial.startTime), 'minute')
						: d.startTime !== initial.startTime;
				const endChanged =
					d.endTime && initial.endTime
						? !dayjs(d.endTime).isSame(dayjs(initial.endTime), 'minute')
						: d.endTime !== initial.endTime;
				return startChanged || endChanged;
			}

			return false;
		});

		if (changedDays.length === 0) {
			showToast('success', 'No changes detected');
			return;
		}

		// Transform the data to match the expected format (only changed days)
		// Include both enabled days (with times) and disabled days (with null times)
		// to properly handle unsetting/removing availability
		const availabilityRequests = changedDays.map((d: any) => ({
			day: d.day.id,
			startTime: d.enabled && d.startTime ? dayjs(d.startTime).format('HH:mm:ss') : null,
			endTime: d.enabled && d.endTime ? dayjs(d.endTime).format('HH:mm:ss') : null,
		}));

		await onSubmit({
			availabilityRequests,
			notes: values.notes,
		});
	};

	const initialValues = React.useMemo(
		() => ({
			availability: allDays.map((day) => {
				// Try to prefill from existing availability
				// Look for availability entries that match this day of week
				const matchingAvails = currentAvailability.filter((avail: UserAvailability) => {
					return avail.day === (day?.['@id'] || avail.day === `/api/days/${day.id}`);
				});

				if (matchingAvails.length > 0) {
					// Get the latest availability for this day (by startAt timestamp)
					const latestAvail = matchingAvails.reduce((latest: UserAvailability, current: UserAvailability) => {
						if (!latest) return current;
						const latestStartAt = dayjs(latest.startAt);
						const currentStartAt = dayjs(current.startAt);
						return currentStartAt.isAfter(latestStartAt) ? current : latest;
					}, matchingAvails[0]);

					// Prefill with the time from latest availability
					const startTime = dayjs(latestAvail.startAt);
					const endTime = dayjs(latestAvail.endAt);
					
					// Check if startAt and endAt are the same (unavailable for entire day)
					const startTimeFormatted = startTime.format('HH:mm:ss');
					const endTimeFormatted = endTime.format('HH:mm:ss');
					const isUnavailable = startTimeFormatted === endTimeFormatted;

					if (isUnavailable) {
						// User is not available for this day - checkbox should be unchecked
						return {
							day,
							enabled: false,
							startTime: null,
							endTime: null,
						};
					}

					// User is available with specific times
					return {
						day,
						enabled: true,
						startTime: startTime,
						endTime: endTime,
					};
				}

				return {
					day,
					enabled: false,
					startTime: null,
					endTime: null,
				};
			}),
			notes: currentAvailability.length > 0 ? 'Updating existing availability' : '',
		}),
		[allDays, currentAvailability]
	);
	console.log('Initial Values:', initialValues.availability);

	// Store initial values for comparison
	React.useEffect(() => {
		if (!initialValuesRef.current && initialValues.availability.length > 0) {
			initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));
		}
	}, [initialValues]);

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box sx={{ mt: 3 }}>
							{daysLoading || userLoading ? (
								<Box
									sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}
								>
									<CircularProgress size={20} color="inherit" />
									<Typography sx={{ ml: 2 }}>Loading availability options...</Typography>
								</Box>
							) : (
								<>
									{/* Display Current Availability Section - Show all 7 days */}
									<Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
										<Typography variant="h6" gutterBottom color="primary">
											Current Availability
										</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
											Your existing availability schedule
										</Typography>
										<Divider sx={{ mb: 2 }} />
										{allDays.map((day, index) => {
											const dayId = day['@id'];
											
											// Check if user has ANY availability set for the week
											const hasAnyAvailabilityInWeek = currentAvailability.length > 0;
											
											// Find the latest availability for this day from currentAvailability
											const matchingAvails = currentAvailability.filter((avail: UserAvailability) => {
												if (!avail.day) return false;
												const availDayId = typeof avail.day === 'string' ? avail.day : avail.day['@id'];
												return availDayId === dayId || availDayId === `/api/days/${day.id}`;
											});
											
											let latestAvail: UserAvailability | null = null;
											if (matchingAvails.length > 0) {
												// Get the latest availability for this day (by startAt timestamp)
												latestAvail = matchingAvails.reduce((latest: UserAvailability, current: UserAvailability) => {
													if (!latest) return current;
													const latestStartAt = dayjs(latest.startAt);
													const currentStartAt = dayjs(current.startAt);
													return currentStartAt.isAfter(latestStartAt) ? current : latest;
												}, matchingAvails[0]);
											}
											
											const dayName = day.label;
											let displayText = '';
											let isUnavailable = false;
											let isNotSet = false;
											
											if (!latestAvail) {
												// No availability set for this day
												// If user has any availability in the week, treat unset days as unavailable
												// Otherwise, show as not set
												if (hasAnyAvailabilityInWeek) {
													displayText = `${dayName} → Not available`;
													isUnavailable = true;
												} else {
													displayText = `${dayName} → Not set`;
													isNotSet = true;
												}
											} else {
												const startTime = dayjs(latestAvail.startAt).format('HH:mm');
												const endTime = dayjs(latestAvail.endAt).format('HH:mm');
												
												// Check if startAt and endAt are the same (unavailable for entire day)
												isUnavailable = startTime === endTime;
												displayText = isUnavailable
													? `${dayName} → Not available`
													: `${dayName} → ${startTime} - ${endTime}`;
											}
											
											return (
												<Box
													key={dayId || index}
													sx={{
														mb: 1.5,
														p: 1.5,
														backgroundColor: isUnavailable ? '#ffebee' : isNotSet ? '#f5f5f5' : 'white',
														borderRadius: 1,
														border: isUnavailable ? '1px solid #ef5350' : isNotSet ? '1px solid #bdbdbd' : '1px solid #e0e0e0',
													}}
												>
													<Typography
														variant="body2"
														fontWeight="medium"
														color={isUnavailable ? 'error' : isNotSet ? 'text.secondary' : 'inherit'}
													>
														{displayText}
													</Typography>
												</Box>
											);
										})}
									</Paper>

									<Typography variant="h6" gutterBottom>
										Update Your Availability
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
										Select the days you are available and specify your preferred time range.
									</Typography>

									{values.availability.map((d: any, index: number) => (
										<Grid
											container
											spacing={2}
											alignItems="center"
											key={d.day['@id']}
											sx={{
												mb: 1,
												borderRadius: 1,
												p: 1,
											}}
										>
											<Grid item xs={4}>
												<FormControlLabel
													control={
														<Checkbox
															checked={d.enabled}
															onChange={(e) => {
																const updated = [...values.availability];
																updated[index].enabled = e.target.checked;

																if (!e.target.checked) {
																	updated[index].startTime = null;
																	updated[index].endTime = null;
																} else {
																	const lastFilledDay = updated
																		.slice(0, index)
																		.reverse()
																		.find(
																			(day: any) => day.enabled && day.startTime && day.endTime
																		);

																	if (lastFilledDay) {
																		updated[index].startTime = dayjs(lastFilledDay.startTime);
																		updated[index].endTime = dayjs(lastFilledDay.endTime);
																	} else {
																		// showToast('success', `Please set start and end times for ${updated[index].day.label}`);
																	}
																}
																setFieldValue('availability', updated);
															}}
														/>
													}
													label={d.day.label}
												/>
											</Grid>

											<Grid item xs={4}>
												<TimePicker
													label="Start Time"
													value={d.startTime}
													onChange={(newTime) => {
														const updated = [...values.availability];
														updated[index].startTime = newTime;
														setFieldValue('availability', updated);
													}}
													disabled={!d.enabled}
													slotProps={{
														textField: {
															fullWidth: true,
														},
													}}
												/>
											</Grid>

											<Grid item xs={4}>
												<TimePicker
													label="End Time"
													value={d.endTime}
													onChange={(newTime) => {
														const updated = [...values.availability];
														updated[index].endTime = newTime;
														setFieldValue('availability', updated);
													}}
													disabled={!d.enabled}
													slotProps={{
														textField: {
															fullWidth: true,
														},
													}}
												/>
											</Grid>
										</Grid>
									))}

									<TextField
										fullWidth
										multiline
										rows={3}
										label="Reason for Change"
										value={values.notes}
										onChange={(e) => setFieldValue('notes', e.target.value)}
										required
										error={touched.notes && Boolean(errors.notes)}
										helperText={touched.notes && errors.notes}
										sx={{ mt: 2 }}
									/>
								</>
							)}
						</Box>
					</LocalizationProvider>
				</Form>
			)}
		</Formik>
	);
};

export default AvailabilityForm;
